<!--
*  TTTech nerve-ui-components
*  Copyright(c) 2021. TTTech Industrial Automation AG.
*
*  ALL RIGHTS RESERVED.
*
*  Usage of this software, including source code, netlists, documentation,
*  is subject to restrictions and conditions of the applicable license
*  agreement with TTTech Industrial Automation AG or its affiliates.
*
*  All trademarks used are the property of their respective owners.
*
*  TTTech Industrial Automation AG and its affiliates do not assume any liability
*  arising out of the application or use of any product described or shown
*  herein. TTTech Industrial Automation AG and its affiliates reserve the right to
*  make changes, at any time, in order to improve reliability, function or
*  design.
*
*  Contact Information:
*  support@tttech-industrial.com
*
*  TTTech Industrial Automation AG, Schoenbrunnerstrasse 7, 1040 Vienna, Austria
*
* -->

<template>
  <v-data-table
    v-if="columns"
    id="iiotDatabaseTable"
    :headers="columns"
    :items="items"
    :no-data-text="$t('database.emptyList')"
    :calculate-widths="true"
    :hide-default-footer="items.length === 0"
    :hide-default-header="true"
    :server-items-length="totalRows"
    :footer-props="{
      'items-per-page-options': [10, 25, 50, 100, 200],
      'items-per-page-text': $t('defaultTable.rowsPerPage'),
    }"
    :class="{ emptyTablePadding: items.length < 1 }"
    class="database-table"
    data-cy="iiotDatabaseTable"
    fixed-header
    @update:page="updateParams($event, 'page')"
    @update:items-per-page="updateParams($event, 'itemsPerPage')"
  >
    <template #header="{ props }">
      <thead :class="'v-data-table-header'">
        <tr>
          <th
            v-for="header in props.headers"
            :key="header.value"
            :class="[
              header.sortable ? 'sortable' : 'unsortable',
              params.sortBy[0] == header.value ? 'active' : '',
              params.sortDesc[0] ? 'desc' : 'asc',
            ]"
            scope="col"
            class="v-data-table-header"
            @click="customSort(header)"
          >
            <v-row class="db-table-header-row ml-1">
              <div>
                {{ header.text }}
              </div>
              <div class="mt-n1">
                <v-icon v-if="header.text !== 'human_readable_timestamp'" class="v-data-table-header__icon" small>
                  mdi-arrow-up
                </v-icon>
              </div>
            </v-row>
          </th>
        </tr>
        <tr>
          <th v-for="header in props.headers" :key="header.text" class="filters">
            <div>
              <v-row>
                <v-col>
                  <v-text-field
                    v-if="header.text !== 'human_readable_timestamp'"
                    :id="`iiotDatabaseTableFilter_${header.value}`"
                    v-model="filter[header.value]"
                    :data-cy="`iiotDatabaseTableFilter_${header.value}`"
                    :placeholder="filterPlaceHolder"
                    class="filter"
                    @keydown.esc="(filter[header.value] = ''), updateParams()"
                    @click:clear="(filter[header.value] = ''), updateParams()"
                    @click:prepend="updateParams()"
                    @keydown.enter="(params.page = 1), updateParams()"
                  />
                </v-col>
              </v-row>
            </div>
          </th>
        </tr>
      </thead>
    </template>
    <template #item="{ item, index }">
      <tr :id="`${id}Row${index}`" tabindex="0">
        <td v-for="header in headers" :key="header.value">
          <div v-if="header.component">
            <component
              :is="header.component.sfc"
              :id="`${id}Select`"
              v-bind="{ ...header.component.props, tableItem: item, index }"
            />
          </div>
          <div v-else>
            {{ handleItem(item, header.value) }}
          </div>
        </td>
      </tr>
    </template>
  </v-data-table>
</template>

<script>
// eslint-disable-next-line import/extensions
import Helper from './helpers/shared.js';

export default {
  name: 'DatabaseTable',
  props: {
    id: {
      type: String,
      default: '',
    },
    database: {
      type: String,
      default: '',
    },
    table: {
      type: String,
      default: '',
    },
    /**
     * Empty state string - make sure it is i18n
     */
    emptyState: {
      type: String,
      required: true,
    },
    /**
     * List of columns consumed by the vuetify table
     */
    columns: {
      type: Array,
      default: () => [],
    },
    /**
     * String representing store module that will be used for
     * actions and getters
     */
    storeModule: {
      type: String,
      default: '',
    },
    /**
     * String that use as placeholder for filter in column
     */
    filterPlaceHolder: {
      type: String,
      default: 'Filter',
    },
    /**
     * Array values shown per page
     */
    customItemsPerPage: {
      type: Array,
      default: () => [],
    },
    customItemsPerPageText: {
      type: String,
      default: '',
    },
    /**
     * String that will call custom store getter instead of default list
     */
    customListGetter: {
      type: String,
      default: '',
    },
    /**
     * String that will call custom store getter for count instead of default count
     */
    customCountGetter: {
      type: String,
      default: '',
    },
    /**
     * Flag to enable/disable pagination
     */
    disablePagination: {
      type: Boolean,
      default: false,
    },
    /**
     * Flag that defines whether the footer will be displayed or not
     */
    hideFooter: {
      type: Boolean,
      default: false,
    },
    /**
     * Flag that defines whether the default header will be displayed or not
     */
    hideDefaultHeader: {
      type: Boolean,
      default: false,
    },

    isFilterColumnsEnabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Store getter which return items that should be checked
     */
    selectedItemsGetter: {
      type: String,
      default: '',
    },
    /**
     * Item property that should be uniquie and which defines checkBox value/state
     */
    itemKey: {
      type: String,
      default: 'id',
    },
  },
  data() {
    return {
      params: {
        itemsPerPage: 10,
        page: 1,
        sortBy: ['timestamp'],
        sortDesc: [true],
      },
      serverParams: {
        columnFilters: {},
        sort: [],
        page: 1,
        perPage: 10,
      },
      checkbox: true,
      filter: {},
      options: {},
      isLoading: false,
    };
  },
  computed: {
    /**
     * Fetch items for the table to consume from the store module either using custom list getter
     * or default getter list
     */
    items() {
      if (
        this.customListGetter &&
        typeof this.$store.getters[`${this.storeModule}/${this.customListGetter}`] === 'function'
      ) {
        return this.$store.getters[`${this.storeModule}/${this.customListGetter}`]();
      }
      return this.$store.getters[`${this.storeModule}/${this.customListGetter || 'list'}`];
    },
    /**
     * Fetch items count from store state for the appropriate state
     */
    totalRows() {
      if (
        this.customListGetter &&
        typeof this.$store.getters[
          this.customCountGetter ? `${this.storeModule}/${this.customCountGetter}` : `${this.storeModule}/count`
        ] === 'function'
      ) {
        return this.$store.getters[
          this.customCountGetter ? `${this.storeModule}/${this.customCountGetter}` : `${this.storeModule}/count`
        ]();
      }
      return this.$store.getters[
        this.customCountGetter ? `${this.storeModule}/${this.customCountGetter}` : `${this.storeModule}/count`
      ];
    },

    /**
     * Compute headers for the table to consume depending if the menu is enabled
     */
    headers() {
      return this.columns;
    },
    /**
     * Fetch selectedItems for the table to consume from the store module getter
     */
    selectedItems: {
      get() {
        return this.$store.getters[`${this.storeModule}/${this.selectedItemsGetter}`];
      },
      set() {},
    },
    defaultRowsPerPageText() {
      return this.$t('defaultTable.rowsPerPage');
    },
  },
  methods: {
    async updateParams(parameters, tag) {
      if (parameters && tag) {
        this.params[tag] = parameters;
      }
      this.serverParams.page = this.params.page;
      this.serverParams.perPage = this.params.itemsPerPage;
      if (this.filter) {
        this.serverParams.columnFilters = Object.fromEntries(
          // eslint-disable-next-line no-unused-vars
          Object.entries(this.filter).filter(([_, v]) => v !== ''),
        );
      }
      if (this.params.sortDesc && this.params.sortDesc.length !== 0) {
        this.serverParams.sort = [
          {
            field: this.params.sortBy[0],
            type: this.params.sortDesc[0] ? 'desc' : 'asc',
          },
        ];
      } else {
        this.serverParams.sort = [
          {
            field: 'timestamp',
            type: 'desc',
          },
        ];
      }
      await this.$store.dispatch('database/dataOfTable', [
        {
          db: this.database,
          table: this.table,
          serverParams: this.serverParams,
          setColumn: true,
        },
        { checkbox: this.checkbox },
      ]);
    },
    customSort(column) {
      if (!column.sortable) {
        return;
      }
      this.params.sortBy[0] = column.text;
      if (this.params.sortDesc[0] === false) {
        this.params.sortDesc = [true];
      } else if (this.params.sortDesc[0] === true) {
        this.params.sortBy = [];
        this.params.sortDesc = [];
      } else {
        this.params.sortDesc = [false];
      }
      this.updateParams();
    },
    /**
     * Concatetinate array  value by key with , except the last place
     * @param array - array to iterate trough
     * @param key - key to access value of the each object in collection
     * @returns string
     */
    handleArrayItemType(array, key) {
      return array.reduce((acc, val, index) => `${acc} ${val[key]}${index + 1 < array.length ? ',' : ''}`, '');
    },

    /**
     * Enable user to use dot notation ('key.subKey') to
     * handle nested data of the table entity for arrays
     * (concatetinate values) and objects.
     *
     * @param item - table item
     * @param key - column key
     * @returns {string|*}
     */
    handleItem(item, key) {
      const splitKeyArray = key.split('.');
      // value is first nested property
      const value = item[splitKeyArray[0]];

      if (!value) {
        return item[key];
      }

      if (Array.isArray(value)) {
        return this.handleArrayItemType(value, splitKeyArray[1]);
      }
      if (Helper.isObject(value)) {
        return value[splitKeyArray[1]];
      }

      return item[splitKeyArray[0]];
    },
  },
};
</script>

<style lang="scss">
.db-table-header-row {
  flex-wrap: nowrap;
}
.unsortable {
  padding-bottom: 2px !important;
}
.database-table > .v-data-table__wrapper {
  max-height: 50vh !important;
}

.filter {
  min-width: 100px;
}
.filters {
  .v-text-field__details {
    display: none;
  }
  .v-input {
    padding-top: 0px;
    margin-top: 0px;
  }
}
.database-table > .v-data-table__wrapper > table > tbody {
  display: table-row-group;
}

.database-table > {
  .v-data-table__wrapper {
    overflow-y: auto;
    overflow-x: auto;
    max-height: 70vh;
    table {
      width: 98%;
    }
  }

  .v-data-table__wrapper > table {
    padding-top: 0;
    border-collapse: separate;
    border-spacing: 0 12px !important;

    thead {
      tr {
        background: transparent;

        &:hover {
          background-color: transparent !important;
        }
      }
    }

    tr {
      height: 43px;
      background-color: #f0f3f5;
      border-radius: 3px;
      &:hover {
        background-color: rgba(226, 243, 255, 0.6) !important;
      }
      &:focus {
        outline: none !important;
      }

      td,
      th {
        border: none !important;
      }
    }
  }
  .v-data-footer {
    border: none !important;
  }
}
</style>
